import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Skeleton,
  IconButton,
  Divider,
  Avatar,
} from "@mui/material";
import {
  CategoriesCreateDropDown,
  getSubCategories,
} from "services/apiService";
import { faTimes, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface LinkGeneratorProps {
  open: boolean;
  onClose?: () => void;
  onCategorySelect: (value: any | null) => void;
}

interface Item {
  id: number;
  category_name: string;
  category_image: string;
}

interface SubCategory extends Item {
  parent_id: number;
}

const CategoriesModel: React.FC<LinkGeneratorProps> = ({
  open,
  onClose,
  onCategorySelect,
}) => {
  const [categories, setCategories] = useState<Item[]>([]);
  const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const AppUrl = process.env.REACT_APP_IMAGE_URL;

  useEffect(() => {
    const fetchData = async () => {
      if (!open) return;
      setLoading(true);
      try {
        const [CategoriesResult, SubCategoriesResult] = await Promise.all([
          CategoriesCreateDropDown(),
          getSubCategories(),
        ]);

        if (CategoriesResult?.data) setCategories(CategoriesResult.data);
        if (SubCategoriesResult?.data)
          setSubCategories(SubCategoriesResult.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open]);

  const handleSelect = (item: Item) => setSelectedItem(item);

  const handleGenerateLink = () => {
    onCategorySelect(selectedItem);
    setSelectedItem(null);
    onClose?.();
  };

  const filteredCategories = categories.filter((category) => {
    const isCategoryMatch = category.category_name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

    const matchingSubCategories = subCategories.filter(
      (sub) =>
        sub.parent_id === category.id &&
        sub.category_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const matchingNestedSubCategories = subCategories.filter((nestedSub) =>
      matchingSubCategories.some(
        (sub) =>
          nestedSub.parent_id === sub.id &&
          nestedSub.category_name
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
      )
    );
    return (
      isCategoryMatch ||
      matchingSubCategories.length > 0 ||
      matchingNestedSubCategories.length > 0
    );
  });

  return (
    <Modal open={open}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 500,
          bgcolor: "white",
          borderRadius: 2,
          boxShadow: 24,
          p: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Select an Item
          </Typography>
          <IconButton onClick={onClose} sx={{ color: "gray" }}>
            <FontAwesomeIcon icon={faTimes} />
          </IconButton>
        </Box>

        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
        />
        <List sx={{ maxHeight: 500, overflowY: "auto", mb: 2 }}>
          {loading ? (
            Array.from(new Array(5)).map((_, index) => (
              <ListItem key={index} disablePadding>
                <ListItemButton>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Skeleton variant="text" width="80%" sx={{ ml: 2 }} />
                </ListItemButton>
              </ListItem>
            ))
          ) : filteredCategories.length > 0 ? (
            filteredCategories.map((category) => {
              const matchingSubCategories = subCategories.filter(
                (sub) =>
                  sub.parent_id === category.id &&
                  sub.category_name
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase())
              );

              return (
                <React.Fragment key={category.id}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleSelect(category)}
                      selected={selectedItem?.id === category.id}
                    >
                      <img
                        src={AppUrl + category.category_image}
                        alt={category.category_name}
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          marginRight: 12,
                        }}
                      />
                      <ListItemText
                        primary={category.category_name}
                        sx={{ fontWeight: 600 }}
                      />
                      {selectedItem?.id === category.id && (
                        <FontAwesomeIcon icon={faCheckCircle} color="green" />
                      )}
                    </ListItemButton>
                  </ListItem>

                  {matchingSubCategories.map((sub) => {
                    const matchingNestedSubCategories = subCategories.filter(
                      (nestedSub) =>
                        nestedSub.parent_id === sub.id &&
                        nestedSub.category_name
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase())
                    );

                    return (
                      <React.Fragment key={sub.id}>
                        <ListItem
                          disablePadding
                          sx={{
                            pl: 4,
                            py: 0.5,
                            position: "relative",
                          }}
                        >
                          <ListItemButton
                            onClick={() => handleSelect(sub)}
                            selected={selectedItem?.id === sub.id}
                            sx={{
                              borderLeft: "1px solid grey",
                            }}
                          >
                            <Avatar
                              src={AppUrl + sub.category_image}
                              alt={sub.category_name}
                              sx={{ width: 30, height: 30, mr: 2 }}
                            />
                            <ListItemText
                              primary={sub.category_name}
                              sx={{ fontSize: "0.9rem" }}
                            />
                            {selectedItem?.id === sub.id && (
                              <FontAwesomeIcon
                                icon={faCheckCircle}
                                color="green"
                              />
                            )}
                          </ListItemButton>
                        </ListItem>

                        {matchingNestedSubCategories.map((nestedSub) => (
                          <ListItem
                            key={nestedSub.id}
                            disablePadding
                            sx={{
                              pl: 8,
                              py: 0.3,
                              position: "relative",
                            }}
                          >
                            <ListItemButton
                              onClick={() => handleSelect(nestedSub)}
                              selected={selectedItem?.id === nestedSub.id}
                              sx={{
                                borderLeft: "1px dashed grey",
                              }}
                            >
                              <Avatar
                                src={AppUrl + nestedSub.category_image}
                                alt={nestedSub.category_name}
                                sx={{ width: 25, height: 25, mr: 2 }}
                              />
                              <ListItemText
                                primary={nestedSub.category_name}
                                sx={{ fontSize: "0.85rem", color: "gray" }}
                              />
                              {selectedItem?.id === nestedSub.id && (
                                <FontAwesomeIcon
                                  icon={faCheckCircle}
                                  color="green"
                                />
                              )}
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </React.Fragment>
                    );
                  })}

                  <Divider sx={{ borderBottomWidth: 2, my: 1 }} />
                </React.Fragment>
              );
            })
          ) : (
            <Typography sx={{ textAlign: "center", py: 2, color: "gray" }}>
              No results found.
            </Typography>
          )}
        </List>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          sx={{ mt: 2 }}
          onClick={handleGenerateLink}
          disabled={!selectedItem}
        >
          select
        </Button>
      </Box>
    </Modal>
  );
};

export default CategoriesModel;
